import { useMemo } from 'react';
import * as THREE from 'three';
import { SketchCanvasOverlayHtml } from './SketchCanvasOverlayHtml';
import { useTool } from '../../state/store';
import { useSketchStore } from '../../state/useSketchStore';
import { planeBasis } from '../../core/sketch/triangulate';
import { offsetLoop as offsetLoopPreview } from '../../core/sketch/offset';
import { circularLoop, arcLoopExact } from '../../core/sketch/curves';
import {
    useSketchLineTool, useSketchRectTool, useSketchCircleTool,
    useSketchArcTool, SketchDrawPreviews,
} from './SketchDrawTools';
import {
    useSketchPushPullTool, useSketchMoveTool, useSketchRotateTool,
    useSketchOffsetTool, useSketchEraseTool, useSketchScaleTool,
    useSketchShiftDragPushPull, useSketchEdgeDragMove, useSketchVertexDragMove,
    useSketchLooseEdgeDragMove, useSketchLooseVertexDragMove,
    useSketchAnnotationDragMove, type LooseStretchItem,
    type PushSnapHit,
} from './SketchModifyTools';
import {
    useSketchTapeMeasureTool, useSketchPaintBucketTool,
    useSketchFollowMeTool, useSketchSectionPlaneTool,
    useSketchInsertComponentTool,
} from './SketchUtilityTools';
import {
    useSketchFreehandTool, useSketchRotatedRectTool,
    useSketchArc3ptTool, useSketchPieTool,
} from './SketchAdvancedDrawTools';
import {
    useSketchProtractorTool, useSketchDimensionTool,
    useSketchTextTool, useSketchText3dTool, useSketchAxesTool,
    protractorArcPreview,
} from './SketchMeasureTools';
import {
    useSketchOrbitTool, useSketchPanTool, useSketchZoomTool,
    useSketchZoomWindowTool, useSketchZoomExtentsTool,
    useSketchZoomSelectionTool, useSketchAlignViewTool,
    useSketchPositionCameraTool, useSketchLookAroundTool, useSketchWalkTool,
    useSketchStandardViewTool, type SketchStandardView,
} from './SketchCameraTools';
import {
    useSketchUcsWorldTool, useSketchUcsFaceTool, useSketchUcsViewTool,
} from './SketchUcsTools';
import {
    useSketchUnionTool, useSketchSubtractTool, useSketchIntersectTool,
    useSketchTrimTool, useSketchSplitTool, useSketchOuterShellTool,
} from './SketchSolidTools';import {
    useSketchFromScratchTool, useSketchFromContoursTool,
    useSketchSmooveTool, useSketchStampTool,
    useSketchDrapeTool, useSketchAddDetailTool,
} from './SketchSandboxTools';
import {
    SketchLoopPreview, SketchInferenceMarker, SketchPolyPreview, SketchAxisLinePreview,
    SketchPrismPreview, SketchGhostSolidPreview,
} from './previews';
import { SketchMarqueeScope } from './SketchSelectMarquee';
import { SKETCH_COLORS } from '../../themes/colors';
import {
    rotationMatrix, transformSolidEdges, transformSolidFaces, transformSolidCorners,
    translationMatrix,
} from '../../core/sketch/transform';
import { solidVertexPoints } from '../../core/sketch/topology';
import type { SketchSolid } from '../../core/sketch/types';
import type { InferenceResult } from '../../core/sketch/inference';

/**
 * Cursor → SketchDrawPreviews inference prop. Carries the axis identity so
 * on-axis feedback takes the Sketch axis colors (red X / green Y / blue Z),
 * plus the dotted from-point alignment guides when alignment inference is
 * engaged.
 */
function sketchInference(
    cursor: InferenceResult | null
): {
    point: THREE.Vector3;
    kind: InferenceResult['kind'];
    axis?: InferenceResult['axis'];
    guideLines?: InferenceResult['guideLines'];
} | null {
    return cursor
        ? { point: cursor.point, kind: cursor.kind, axis: cursor.axis, guideLines: cursor.guideLines }
        : null;
}

/** Shared identity transform for highlight-only ghost previews. */
const IDENTITY_MATRIX = new THREE.Matrix4();

/**
 * Sketch tool scope — mounts ONLY the active tool's hook (lazy per-tool
 * scopes, mirroring rendering/ToolScope's pattern) and renders its preview.
 * Pointer events arrive through the tool bus (sketchToolBus), so scopes
 * here are pure hooks + preview JSX.
 */

function rectLoopPreview(corner: THREE.Vector3, cursor: THREE.Vector3, normal: THREE.Vector3, lock: string | null): THREE.Vector3[] {
    const second = cursor.clone();
    if (lock === 'x') second.setY(corner.y);
    if (lock === 'y') second.setX(corner.x);
    const { u, v } = planeBasis(normal);
    const du = second.clone().sub(corner);
    return [
        corner.clone(),
        corner.clone().addScaledVector(u, du.dot(u)),
        second.clone(),
        corner.clone().addScaledVector(v, du.dot(v)),
    ];
}

function LineScope() {
    const { points, cursor } = useSketchLineTool();
    // Sketch face rule: a surface preview appears ONLY when the stroke is
    // actually completing — the cursor inferred onto the first point (same
    // test the tool uses to close). An open polyline never previews a face.
    const closing = !!cursor && points.length >= 3
        && cursor.point.distanceTo(points[0]) < 1e-4;
    const rubber = useMemo(
        () => (cursor && points.length > 0
            ? { points: [...points, cursor.point] }
            : points.length > 0 ? { points } : null),
        [points, cursor]
    );
    return (
        <SketchDrawPreviews
            rubber={rubber}
            loop={closing ? [...points, points[0]] : null}
            inference={sketchInference(cursor)}
            axisLine={cursor?.axisLine ?? null}
            axis={cursor?.axis}
        />
    );
}

function RectScope() {
    const { corner, cursor, planeNormal } = useSketchRectTool();
    const loop = useMemo(() => {
        if (!corner || !cursor) return null;
        // Preview follows the live drawing plane (ground, face, sticky).
        return rectLoopPreview(corner, cursor.point, planeNormal, cursor.lockedAxis);
    }, [corner, cursor, planeNormal]);
    return (
        <SketchDrawPreviews
            loop={loop}
            inference={sketchInference(cursor)}
            axisLine={cursor?.axisLine ?? null}
            axis={cursor?.axis}
        />
    );
}

function CircleScope({ sides = 0 }: { sides?: number }) {
    const { center, cursor, planeNormal, sideCount } = useSketchCircleTool(sides);
    const loop = useMemo(() => {
        if (!center || !cursor) return null;
        const radius = center.distanceTo(cursor.point);
        if (radius < 1e-6) return null;
        const { u, v } = planeBasis(planeNormal);
        const count = sideCount > 2 ? sideCount : 24;
        const out: THREE.Vector3[] = [];
        for (let i = 0; i < count; i++) {
            const angle = (i / count) * Math.PI * 2;
            out.push(
                center.clone()
                    .addScaledVector(u, Math.cos(angle) * radius)
                    .addScaledVector(v, Math.sin(angle) * radius)
            );
        }
        return out;
    }, [center, cursor, sideCount, planeNormal]);
    return (
        <SketchDrawPreviews
            loop={loop}
            inference={sketchInference(cursor)}
            axisLine={cursor?.axisLine ?? null}
            axis={cursor?.axis}
        />
    );
}

function ArcScope() {
    const { start, end, cursor } = useSketchArcTool();
    const preview = useMemo(() => {
        if (start && end && cursor) {
            return [...bezierLoopLocal(start, cursor.point, end, 16), start];
        }
        return null;
    }, [start, end, cursor]);
    const rubber = useMemo(() => {
        if (!start || end) return null;
        if (!cursor) return null;
        return { points: [start, cursor.point] };
    }, [start, end, cursor]);
    return (
        <SketchDrawPreviews
            rubber={rubber}
            loop={preview}
            inference={sketchInference(cursor)}
            axisLine={cursor?.axisLine ?? null}
            axis={cursor?.axis}
        />
    );
}

function bezierLoopLocal(p0: THREE.Vector3, p1: THREE.Vector3, p2: THREE.Vector3, segments = 16): THREE.Vector3[] {
    const out: THREE.Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const a = p0.clone().lerp(p1, t);
        const b = p1.clone().lerp(p2, t);
        out.push(a.lerp(b, t));
    }
    return out;
}

/** Push/Pull: live extrusion preview — the picked face's contour lifted by
 *  the drag distance, wired to the base with wall quads and vertical edges
 *  (holes lift and wall too), so the gesture reads as the solid being born
 *  rather than a detached surface hovering over the model. The preview
 *  persists while the gesture is armed even if the cursor inference drops
 *  out; the inference dot keeps its standard role. */
function PushPullScope() {
    const { active, cursor, previewDistance, snapMarker } = useSketchPushPullTool();
    const face = useMemo(() => {
        if (!active) return null;
        const solid = useSketchStore.getState().solids[active.face.solidId];
        return solid?.faces.find((f) => f.id === active.face.faceId) ?? null;
    }, [active]);
    if (!active || !face) return null;
    const baseLoop = face.outer.map((p) => new THREE.Vector3(...p));
    const holes = face.holes.map((h) => h.map((p) => new THREE.Vector3(...p)));
    const normal = new THREE.Vector3(...face.normal).normalize();
    return (
        <group>
            <SketchPrismPreview
                baseLoop={baseLoop}
                holes={holes}
                normal={normal}
                distance={previewDistance}
            />
            {cursor && <SketchInferenceMarker point={cursor.point} kind={cursor.kind} axis={cursor.axis} />}
            {snapMarker && <SketchInferenceMarker point={snapMarker.point} kind={snapMarker.kind} />}
        </group>
    );
}

/** Move/Copy: ghost wireframe of every targeted solid at the predicted
 *  transform, plus the base→cursor rubber line for the drag vector. Face
 *  targets preview the DEFORMED solid — the face carries its shared corners
 *  and the side faces stretch, matching the commit path. */
function MoveScope({ copy = false }: { copy?: boolean }) {
    const { base, cursor } = useSketchMoveTool(copy);
    const solids = useSketchStore((s) => s.solids);
    const ghost = useMemo(() => {
        if (!base || !cursor) return null;
        const delta = cursor.point.clone().sub(base.origin);
        if (delta.lengthSq() < 1e-9) return null;
        return new THREE.Matrix4().makeTranslation(delta.x, delta.y, delta.z);
    }, [base, cursor]);
    const faceGhosts = useMemo(() => {
        if (!base || base.faces.length === 0 || !ghost) return null;
        const bySolid = new Map<string, string[]>();
        base.faces.forEach(({ solidId, faceId }) => {
            const list = bySolid.get(solidId) ?? [];
            if (!list.includes(faceId)) list.push(faceId);
            bySolid.set(solidId, list);
        });
        const out: SketchSolid[] = [];
        bySolid.forEach((faceIds, solidId) => {
            const solid = solids[solidId];
            if (solid) out.push(transformSolidFaces(solid, faceIds, ghost));
        });
        return out;
    }, [base, ghost, solids]);
    const edgeGhosts = useMemo(() => {
        if (!base || base.edges.length === 0 || !ghost) return null;
        const bySolid = new Map<string, string[]>();
        base.edges.forEach(({ solidId, edgeKey }) => {
            const list = bySolid.get(solidId) ?? [];
            if (!list.includes(edgeKey)) list.push(edgeKey);
            bySolid.set(solidId, list);
        });
        const out: SketchSolid[] = [];
        bySolid.forEach((edgeKeys, solidId) => {
            const solid = solids[solidId];
            if (solid) out.push(transformSolidEdges(solid, edgeKeys, ghost));
        });
        return out;
    }, [base, ghost, solids]);
    return (
        <group>
            {ghost && base && edgeGhosts
                ? edgeGhosts.map((solid, i) => (
                    <SketchGhostSolidPreview key={`edge-${i}`} solid={solid} matrix={IDENTITY_MATRIX} />
                ))
                : ghost && base && faceGhosts
                    ? faceGhosts.map((solid, i) => (
                        <SketchGhostSolidPreview key={`face-${i}`} solid={solid} matrix={IDENTITY_MATRIX} />
                    ))
                    : ghost && base && base.solidIds.map((id) => (
                        solids[id]
                            ? <SketchGhostSolidPreview key={id} solid={solids[id]} matrix={ghost} />
                            : null
                    ))}
            <SketchDrawPreviews
                rubber={base && cursor ? { points: [base.origin, cursor.point] } : null}
                inference={sketchInference(cursor)}
            />
        </group>
    );
}

/** Rotate: after the reference direction is locked, the ghost solid shows
 *  the Z rotation the next click would commit (same signed-angle model as
 *  the commit path); the center→cursor rubber line keeps the protractor feel.
 *  Edge selections ghost the DEFORMED solids (connected faces follow). */
function RotateScope() {
    const { state, cursor } = useSketchRotateTool();
    const solids = useSketchStore((s) => s.solids);
    const ghost = useMemo(() => {
        if (!state || !cursor || !state.ref) return null;
        const refDir = state.ref.clone().sub(state.center).setZ(0);
        const curDir = cursor.point.clone().sub(state.center).setZ(0);
        if (refDir.lengthSq() < 1e-12 || curDir.lengthSq() < 1e-12) return null;
        let angle = refDir.normalize().angleTo(curDir.normalize());
        if (refDir.x * curDir.y - refDir.y * curDir.x < 0) angle = -angle;
        if (Math.abs(angle) < 1e-4) return null;
        return rotationMatrix(new THREE.Vector3(0, 0, 1), angle, state.center);
    }, [state, cursor]);
    const edgeGhosts = useMemo(() => {
        if (!state || state.edges.length === 0 || !ghost) return null;
        const bySolid = new Map<string, string[]>();
        state.edges.forEach(({ solidId, edgeKey }) => {
            const list = bySolid.get(solidId) ?? [];
            if (!list.includes(edgeKey)) list.push(edgeKey);
            bySolid.set(solidId, list);
        });
        const out: SketchSolid[] = [];
        bySolid.forEach((edgeKeys, solidId) => {
            const solid = solids[solidId];
            if (solid) out.push(transformSolidEdges(solid, edgeKeys, ghost));
        });
        return out;
    }, [state, ghost, solids]);
    const solid = state && state.solidIds.length > 0 ? solids[state.solidIds[0]] : null;
    return (
        <group>
            {edgeGhosts
                ? edgeGhosts.map((deformed, i) => (
                    <SketchGhostSolidPreview key={`edge-${i}`} solid={deformed} matrix={IDENTITY_MATRIX} />
                ))
                : solid && ghost && <SketchGhostSolidPreview solid={solid} matrix={ghost} />}
            <SketchDrawPreviews
                rubber={state && cursor ? { points: [state.center, cursor.point] } : null}
                inference={sketchInference(cursor)}
            />
        </group>
    );
}

/** Offset: live inner-loop preview on the picked face. */
function OffsetScope() {
    const { active, cursor, previewDistance } = useSketchOffsetTool();
    const loop = useMemo(() => {
        if (!active || !cursor) return null;
        const solid = useSketchStore.getState().solids[active.face.solidId];
        const face = solid?.faces.find((f) => f.id === active.face.faceId);
        if (!face) return null;
        return offsetLoopPreview(face.outer, active.normal, previewDistance)
            .map((p) => new THREE.Vector3(...p));
    }, [active, cursor, previewDistance]);
    return (
        <SketchDrawPreviews
            loop={loop}
            inference={sketchInference(cursor)}
        />
    );
}

/** Tape Measure: rubber line while measuring, magenta parallel-guide line
 *  while the offset-guide mode is armed on a model edge. */
function TapeScope() {
    const { first, edgeRef, cursor } = useSketchTapeMeasureTool();
    if (!first || !cursor) return null;
    return (
        <group>
            {edgeRef ? (
                <SketchAxisLinePreview line={{ a: cursor.clone().addScaledVector(edgeRef.b.clone().sub(edgeRef.a).normalize(), -1000), b: cursor.clone().addScaledVector(edgeRef.b.clone().sub(edgeRef.a).normalize(), 1000) }} />
            ) : (
                <SketchPolyPreview vertices={[first]} cursor={cursor} />
            )}
        </group>
    );
}

/** Wrap the side-effect-only tools so hooks stay rule-clean per component. */
function PaintBucketScope() {
    useSketchPaintBucketTool();
    return null;
}

function SectionPlaneScope() {
    useSketchSectionPlaneTool();
    return null;
}

function EraseScope() {
    useSketchEraseTool();
    return null;
}

/** Insert Component: placement ghost under the cursor. */
function InsertComponentScope() {
    const { ghost } = useSketchInsertComponentTool();
    if (!ghost) return null;
    return <SketchInferenceMarker point={ghost} kind="center" />;
}

/** Active-tool switch (no default renderer: selection needs no preview). */
function ActiveToolScope() {
    const tool = useTool();
    switch (tool) {
        case 'line': return <LineScope />;
        case 'rectangle': return <RectScope />;
        case 'circle': return <CircleScope />;
        case 'polygon': return <CircleScope sides={6} />;
        case 'arc': return <ArcScope />;
        case 'freehand': return <FreehandScope />;
        case 'rotatedRectangle': return <RotatedRectScope />;
        case 'arc3pt': return <Arc3ptScope />;
        case 'pie': return <PieScope />;
        case 'pushPull': return <PushPullScope />;
        case 'MOVE': case 'move': return <MoveScope />;
        case 'COPY': case 'copy': return <MoveScope copy />;
        case 'ROTATE': case 'rotate': return <RotateScope />;
        case 'SCALE': case 'scale': return <ScaleScope />;
        case 'OFFSET': case 'offset': return <OffsetScope />;
        case 'tapeMeasure': return <TapeScope />;
        case 'paintBucket': return <PaintBucketScope />;
        case 'followMe': return <FollowMeScope />;
        case 'sectionPlane': return <SectionPlaneScope />;
        case 'DELETE': case 'erase': return <EraseScope />;
        case 'insertComponent': return <InsertComponentScope />;
        case 'protractor': return <ProtractorScope />;
        case 'dimension': return <DimensionScope />;
        case 'sketchText': return <TextLabelScope />;
        case 'text3d': return <Text3dScope />;
        case 'axes': return <AxesScope />;
        case 'ucsWorld': return <UcsWorldScope />;
        case 'ucsFace': return <UcsFaceScope />;
        case 'ucsView': return <UcsViewScope />;
        // Camera / navigation
        case 'orbit': case 'ORBIT': return <OrbitScope />;
        case 'pan': case 'PAN': return <PanScope />;
        case 'zoom': case 'ZOOM': return <ZoomScope />;
        case 'zoomWindow': return <ZoomWindowScope />;
        case 'zoomExtents': return <ZoomExtentsScope />;
        case 'zoomSelection': return <ZoomSelectionScope />;
        case 'alignView': return <AlignViewScope />;
        case 'positionCamera': return <PositionCameraScope />;
        case 'lookAround': return <LookAroundScope />;
        case 'walk': return <WalkScope />;
        // Standard views (one-shot camera poses)
        case 'viewIso': return <StandardViewScope view="iso" />;
        case 'viewTop': return <StandardViewScope view="top" />;
        case 'viewBottom': return <StandardViewScope view="bottom" />;
        case 'viewFront': return <StandardViewScope view="front" />;
        case 'viewBack': return <StandardViewScope view="back" />;
        case 'viewLeft': return <StandardViewScope view="left" />;
        case 'viewRight': return <StandardViewScope view="right" />;
        // Solid tools
        case 'solidUnion': return <UnionScope />;
        case 'solidSubtract': return <SubtractScope />;
        case 'solidIntersect': return <IntersectScope />;
        case 'solidTrim': return <TrimScope />;
        case 'solidSplit': return <SplitScope />;
        case 'solidOuterShell': return <OuterShellScope />;
        // Sandbox
        case 'sandboxFromContours': return <FromContoursScope />;
        case 'sandboxFromScratch': return <FromScratchScope />;
        case 'sandboxSmoove': return <SmooveScope />;
        case 'sandboxStamp': return <StampScope />;
        case 'sandboxDrape': return <DrapeScope />;
        case 'sandboxAddDetail': return <AddDetailScope />;
        default: return null;
    }
}

/** Select-mode Shift+drag push/pull preview: the armed face's contour
 *  lifted by the live drag distance (same prism read as PushPullScope).
 *  While the drag distance aligns with model geometry the snap marker
 *  shows at the target point, like the Push/Pull tool's inference dot. */
function ShiftDragPushPullPreview({ faceRef, distance, snapMarker }: {
    faceRef: { solidId: string; faceId: string };
    distance: number;
    snapMarker: PushSnapHit | null;
}) {
    const solid = useSketchStore((s) => s.solids[faceRef.solidId]);
    const face = solid?.faces.find((f) => f.id === faceRef.faceId);
    if (!face) return null;
    const baseLoop = face.outer.map((p) => new THREE.Vector3(...p));
    const holes = face.holes.map((h) => h.map((p) => new THREE.Vector3(...p)));
    const normal = new THREE.Vector3(...face.normal).normalize();
    return (
        <group>
            <SketchPrismPreview baseLoop={baseLoop} holes={holes} normal={normal} distance={distance} />
            {snapMarker && <SketchInferenceMarker point={snapMarker.point} kind={snapMarker.kind} />}
        </group>
    );
}

/** Select-mode edge-drag preview: the DEFORMED solid ghost (the dragged
 *  chain carries its shared corners; connected faces stretch) while the
 *  gesture is live — the commit path rendered ahead of the pointer. */
function EdgeDragMovePreview({ solidId, edgeKeys, offset }: {
    solidId: string;
    edgeKeys: string[];
    offset: THREE.Vector3;
}) {
    const solid = useSketchStore((s) => s.solids[solidId]);
    const deformed = useMemo(() => {
        if (!solid || offset.lengthSq() < 1e-12) return null;
        return transformSolidEdges(solid, edgeKeys, translationMatrix(offset));
    }, [solid, edgeKeys, offset]);
    if (!deformed) return null;
    return <SketchGhostSolidPreview solid={deformed} matrix={IDENTITY_MATRIX} />;
}

/** Select-mode vertex-drag preview: the DEFORMED solid ghost (the dragged
 *  corner carries every welded face; connected faces stretch) while the
 *  gesture is live — the commit path rendered ahead of the pointer. */
function VertexDragMovePreview({ solidId, vertexKeys, offset }: {
    solidId: string;
    vertexKeys: string[];
    offset: THREE.Vector3;
}) {
    const solid = useSketchStore((s) => s.solids[solidId]);
    const deformed = useMemo(() => {
        if (!solid || offset.lengthSq() < 1e-12) return null;
        const corners = vertexKeys.flatMap((key) => solidVertexPoints(solid, key));
        if (corners.length === 0) return null;
        return transformSolidCorners(solid, corners, translationMatrix(offset));
    }, [solid, vertexKeys, offset]);
    if (!deformed) return null;
    return <SketchGhostSolidPreview solid={deformed} matrix={IDENTITY_MATRIX} />;
}

/** Select-mode loose drag preview (segment move AND endpoint stretch): the
 *  predicted segments rendered ahead of the pointer — a moved edge rides
 *  whole, a stretched endpoint elongates only the welded ends, and the
 *  weld followers show exactly what will follow the commit. */
function LooseStretchPreview({ items, offset }: {
    items: LooseStretchItem[];
    offset: THREE.Vector3;
}) {
    const edges = useSketchStore((s) => s.edges);
    const geometry = useMemo(() => {
        if (offset.lengthSq() < 1e-12) return null;
        const pts: number[] = [];
        items.forEach(({ id, ends }) => {
            const edge = edges[id];
            if (!edge) return;
            const a = new THREE.Vector3(...edge.a);
            const b = new THREE.Vector3(...edge.b);
            if (ends.includes('a')) a.add(offset);
            if (ends.includes('b')) b.add(offset);
            pts.push(a.x, a.y, a.z, b.x, b.y, b.z);
        });
        if (pts.length === 0) return null;
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
        return geo;
    }, [edges, items, offset]);
    if (!geometry) return null;
    return (
        <lineSegments geometry={geometry} raycast={() => null} renderOrder={8}>
            <lineBasicMaterial color={SKETCH_COLORS.faceSelected} depthTest={false} transparent opacity={0.9} />
        </lineSegments>
    );
}

/** Select-mode annotation drag preview: the annotation's lines at the
 *  predicted position while the gesture is live — the dimension's witness
 *  + dim lines re-foot at the moved dim line, the label's leader rides. */
function AnnotationDragPreview({ type, id, offset }: {
    type: 'dimension' | 'label';
    id: string;
    offset: THREE.Vector3;
}) {
    const dimension = useSketchStore((s) => s.dimensions[id]);
    const label = useSketchStore((s) => s.labels[id]);
    const geometry = useMemo(() => {
        if (offset.lengthSq() < 1e-12) return null;
        const pts: number[] = [];
        if (type === 'dimension' && dimension) {
            const a = new THREE.Vector3(...dimension.a);
            const b = new THREE.Vector3(...dimension.b);
            const moved = new THREE.Vector3(...dimension.offset).add(offset);
            const dir = b.clone().sub(a);
            if (dir.lengthSq() < 1e-12) return null;
            dir.normalize();
            const footA = a.clone().addScaledVector(dir, moved.clone().sub(a).dot(dir));
            const footB = b.clone().addScaledVector(dir, moved.clone().sub(b).dot(dir));
            pts.push(
                a.x, a.y, a.z, footA.x, footA.y, footA.z,
                b.x, b.y, b.z, footB.x, footB.y, footB.z,
                footA.x, footA.y, footA.z, footB.x, footB.y, footB.z,
            );
        } else if (type === 'label' && label) {
            const anchor = new THREE.Vector3(...label.anchor).add(offset);
            const leader = new THREE.Vector3(...label.leader).add(offset);
            pts.push(anchor.x, anchor.y, anchor.z, leader.x, leader.y, leader.z);
        } else {
            return null;
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
        return geo;
    }, [type, id, dimension, label, offset]);
    if (!geometry) return null;
    return (
        <lineSegments geometry={geometry} raycast={() => null} renderOrder={8}>
            <lineBasicMaterial color={SKETCH_COLORS.guide} depthTest={false} transparent opacity={0.7} />
        </lineSegments>
    );
}

/** SketchToolScope: the active tool's scope plus the always-mounted
 *  select-mode gestures — Shift+drag push/pull on faces, press-drag move on
 *  solid edges, press-drag move on welded corners, press-drag move on drawn
 *  segments and their endpoints, press-drag repositioning of annotations
 *  and the window/crossing marquee on empty space (the bus starts any of
 *  them only when no tool owns the pointer, so they never render together). */
export function SketchToolScope() {
    const shiftDrag = useSketchShiftDragPushPull();
    const edgeDrag = useSketchEdgeDragMove();
    const vertexDrag = useSketchVertexDragMove();
    const looseEdgeDrag = useSketchLooseEdgeDragMove();
    const looseVertexDrag = useSketchLooseVertexDragMove();
    const annotationDrag = useSketchAnnotationDragMove();
    return (
        <group>
            <ActiveToolScope />
            <SketchMarqueeScope />
            {shiftDrag.active && (
                <ShiftDragPushPullPreview
                    faceRef={shiftDrag.active}
                    distance={shiftDrag.previewDistance}
                    snapMarker={shiftDrag.snapMarker}
                />
            )}
            {edgeDrag.active && edgeDrag.offset && (
                <EdgeDragMovePreview
                    solidId={edgeDrag.active.solidId}
                    edgeKeys={edgeDrag.active.edgeKeys}
                    offset={edgeDrag.offset}
                />
            )}
            {vertexDrag.active && vertexDrag.offset && (
                <VertexDragMovePreview
                    solidId={vertexDrag.active.solidId}
                    vertexKeys={vertexDrag.active.vertexKeys}
                    offset={vertexDrag.offset}
                />
            )}
            {looseEdgeDrag.active && looseEdgeDrag.offset && (
                <LooseStretchPreview
                    items={looseEdgeDrag.active.items}
                    offset={looseEdgeDrag.offset}
                />
            )}
            {looseVertexDrag.active && looseVertexDrag.offset && (
                <LooseStretchPreview
                    items={looseVertexDrag.active.items}
                    offset={looseVertexDrag.offset}
                />
            )}
            {annotationDrag.active && annotationDrag.offset && (
                <AnnotationDragPreview
                    type={annotationDrag.active.type}
                    id={annotationDrag.active.id}
                    offset={annotationDrag.offset}
                />
            )}
        </group>
    );
}

/** Follow Me: profile marker + live path polyline. */
function FollowMeScope() {
    const { profile, path, cursor } = useSketchFollowMeTool();
    const preview = useMemo(() => {
        if (!profile || path.length === 0) return null;
        return cursor ? [...path, cursor] : path;
    }, [profile, path, cursor]);
    return (
        <group>
            {preview && preview.length >= 2 && (
                <SketchLoopPreview loop={preview.length === 2 ? preview : preview} />
            )}
            {profile && <SketchInferenceMarker point={path[0] ?? new THREE.Vector3()} kind="onFace" />}
        </group>
    );
}

// ============================== advanced draw scopes ==============================

function FreehandScope() {
    const { points, cursor } = useSketchFreehandTool();
    return (
        <group>
            <SketchPolyPreview vertices={points} cursor={null} />
            {cursor && <SketchInferenceMarker point={cursor} kind="onFace" />}
        </group>
    );
}

function RotatedRectScope() {
    const { p1, p2, cursor } = useSketchRotatedRectTool();
    const loop = useMemo(() => {
        if (!p1 || !p2 || !cursor) return null;
        const u = p2.clone().sub(p1);
        const uLen = u.length();
        if (uLen < 1e-9) return null;
        u.divideScalar(uLen);
        const raw = cursor.point.clone().sub(p1);
        const v = raw.clone().addScaledVector(u, -raw.dot(u));
        const vLen = v.length();
        if (vLen < 1e-9) return null;
        v.divideScalar(vLen);
        return [
            p1.clone(),
            p2.clone(),
            p2.clone().addScaledVector(v, vLen),
            p1.clone().addScaledVector(v, vLen),
        ];
    }, [p1, p2, cursor]);
    return (
        <SketchDrawPreviews
            rubber={p1 && !p2 && cursor ? { points: [p1, cursor.point] } : null}
            loop={loop}
            inference={sketchInference(cursor)}
        />
    );
}

function Arc3ptScope() {
    const { start, mid, cursor } = useSketchArc3ptTool();
    const preview = useMemo(() => {
        if (start && mid && cursor) {
            return arcLoopExact(start, mid, cursor.point, 20)?.loop ?? null;
        }
        return null;
    }, [start, mid, cursor]);
    const rubber = useMemo(() => {
        if (!cursor) return null;
        if (start && !mid) return { points: [start, cursor.point] };
        return null;
    }, [start, mid, cursor]);
    return (
        <SketchDrawPreviews
            rubber={rubber}
            loop={preview}
            inference={sketchInference(cursor)}
        />
    );
}

function PieScope() {
    const { center, radiusPoint, cursor } = useSketchPieTool();
    const rubber = useMemo(() => {
        if (!cursor) return null;
        if (center && radiusPoint) return { points: [radiusPoint, cursor.point] };
        if (center) return { points: [center, cursor.point] };
        return null;
    }, [center, radiusPoint, cursor]);
    return (
        <SketchDrawPreviews
            rubber={rubber}
            inference={sketchInference(cursor)}
        />
    );
}

// ============================== scale scope ==============================

/** Scale: after the reference distance is locked, the ghost solids show the
 *  uniform factor the next click would commit (|base→cursor| over the
 *  reference, about the shared base point). */
function ScaleScope() {
    const { state, cursor } = useSketchScaleTool();
    const solids = useSketchStore((s) => s.solids);
    const ghost = useMemo(() => {
        if (!state || !cursor || !state.ref) return null;
        const factor = cursor.point.distanceTo(state.base) / state.ref.distanceTo(state.base);
        if (!Number.isFinite(factor) || Math.abs(factor - 1) < 1e-4) return null;
        const b = state.base;
        return new THREE.Matrix4()
            .makeTranslation(b.x, b.y, b.z)
            .multiply(new THREE.Matrix4().makeScale(factor, factor, factor))
            .multiply(new THREE.Matrix4().makeTranslation(-b.x, -b.y, -b.z));
    }, [state, cursor]);
    return (
        <group>
            {ghost && state && state.solidIds.map((id) => (
                solids[id]
                    ? <SketchGhostSolidPreview key={id} solid={solids[id]} matrix={ghost} />
                    : null
            ))}
            <SketchDrawPreviews
                rubber={state && cursor ? { points: [state.base, cursor.point] } : null}
                inference={sketchInference(cursor)}
            />
        </group>
    );
}

// ============================== construction scopes ==============================

function ProtractorScope() {
    const { center, refDir, cursor } = useSketchProtractorTool();
    const arc = useMemo(() => (
        center && refDir && cursor ? protractorArcPreview(center, refDir, cursor.point) : null
    ), [center, refDir, cursor]);
    return (
        <SketchDrawPreviews
            rubber={center && cursor ? { points: [center, cursor.point] } : null}
            loop={arc}
            inference={sketchInference(cursor)}
        />
    );
}

function DimensionScope() {
    const { a, b, cursor } = useSketchDimensionTool();
    const rubber = useMemo(() => {
        if (!cursor) return null;
        if (a && b) return { points: [b, cursor.point] };
        if (a) return { points: [a, cursor.point] };
        return null;
    }, [a, b, cursor]);
    return (
        <SketchDrawPreviews
            rubber={rubber}
            inference={sketchInference(cursor)}
        />
    );
}

function TextLabelScope() {
    const { cursor } = useSketchTextTool();
    if (!cursor) return null;
    return <SketchInferenceMarker point={cursor.point} kind={cursor.kind} axis={cursor.axis} />;
}

function Text3dScope() {
    const { ghost } = useSketchText3dTool();
    if (!ghost) return null;
    return <SketchInferenceMarker point={ghost} kind="center" />;
}

function AxesScope() {
    const { origin, xPoint, cursor } = useSketchAxesTool();
    return (
        <group>
            {origin && xPoint && cursor && (
                <group>
                    <SketchAxisLinePreview line={{ a: origin, b: xPoint }} />
                    <SketchAxisLinePreview line={{ a: origin, b: cursor.point }} />
                </group>
            )}
            {cursor && <SketchInferenceMarker point={cursor.point} kind={cursor.kind} axis={cursor.axis} />}
        </group>
    );
}

// ============================== ucs scopes ==============================

/** World / View UCS run instantly on activation: no preview to render. */
function UcsWorldScope() {
    useSketchUcsWorldTool();
    return null;
}

/** Face UCS: hover feedback only — the axes commit on the face pick. */
function UcsFaceScope() {
    const { cursor } = useSketchUcsFaceTool();
    if (!cursor) return null;
    return <SketchInferenceMarker point={cursor.point} kind={cursor.kind} axis={cursor.axis} />;
}

function UcsViewScope() {
    useSketchUcsViewTool();
    return null;
}

// ============================== camera scopes ==============================

/** Orbit / Pan: left button is remapped on OrbitControls; no preview. */
function OrbitScope() {
    useSketchOrbitTool();
    return null;
}

function PanScope() {
    useSketchPanTool();
    return null;
}

function ZoomScope() {
    useSketchZoomTool();
    return null;
}

function ZoomExtentsScope() {
    useSketchZoomExtentsTool();
    return null;
}

/** One-shot context-menu camera ops (run on mount, then back to Select). */
function ZoomSelectionScope() {
    useSketchZoomSelectionTool();
    return null;
}

function AlignViewScope() {
    useSketchAlignViewTool();
    return null;
}

function PositionCameraScope() {
    useSketchPositionCameraTool();
    return null;
}

function LookAroundScope() {
    useSketchLookAroundTool();
    return null;
}

function WalkScope() {
    useSketchWalkTool();
    return null;
}

/** Standard Views: one-shot camera poses (run on activation, then back to
    Select). The view prop drives the pose; switching between two presets
    re-runs the pose effect through it. */
function StandardViewScope({ view }: { view: SketchStandardView }) {
    useSketchStandardViewTool(view);
    return null;
}

/** Zoom Window: the screen-space rubber rectangle as an HTML overlay. */
function ZoomWindowScope() {
    const { rect } = useSketchZoomWindowTool();
    if (!rect) return null;
    return (
        <SketchCanvasOverlayHtml>
            <div
                style={{
                    position: 'absolute',
                    left: rect.x0,
                    top: rect.y0,
                    width: rect.x1 - rect.x0,
                    height: rect.y1 - rect.y0,
                    border: '1px solid #e04938',
                    background: 'rgba(224, 73, 56, 0.08)',
                    pointerEvents: 'none',
                }}
            />
        </SketchCanvasOverlayHtml>
    );
}

// ============================== solid tool scopes ==============================

function UnionScope() {
    const { picks } = useSketchUnionTool();
    return <PickedSolidHighlights picks={picks} />;
}

function SubtractScope() {
    const { picks } = useSketchSubtractTool();
    return <PickedSolidHighlights picks={picks} />;
}

function IntersectScope() {
    const { picks } = useSketchIntersectTool();
    return <PickedSolidHighlights picks={picks} />;
}

function TrimScope() {
    const { picks } = useSketchTrimTool();
    return <PickedSolidHighlights picks={picks} />;
}

function SplitScope() {
    const { picks } = useSketchSplitTool();
    return <PickedSolidHighlights picks={picks} />;
}

function OuterShellScope() {
    const { picks } = useSketchOuterShellTool();
    return <PickedSolidHighlights picks={picks} />;
}

/** Selection-colored edge highlight over the solids already picked for a
 *  boolean — the pick state reads off the geometry itself instead of a
 *  detached center dot. */
function PickedSolidHighlights({ picks }: { picks: string[] }) {
    const solids = useSketchStore((s) => s.solids);
    return (
        <group>
            {picks.map((id) => (
                solids[id]
                    ? (
                        <SketchGhostSolidPreview
                            key={id}
                            solid={solids[id]}
                            matrix={IDENTITY_MATRIX}
                            color={SKETCH_COLORS.faceSelected}
                            showFaces={false}
                        />
                    )
                    : null
            ))}
        </group>
    );
}

// ============================== sandbox scopes ==============================

function FromScratchScope() {
    const { corner, cursor } = useSketchFromScratchTool();
    return (
        <SketchDrawPreviews
            rubber={corner && cursor ? { points: [corner, cursor.point] } : null}
            inference={sketchInference(cursor)}
        />
    );
}

/** Smoove / Stamp: the working radius circle around the picked center. */
function RadiusCircle({ center, radius, color }: {
    center: THREE.Vector3;
    radius: number;
    color: string;
}) {
    const loop = useMemo(
        () => circularLoop(center, radius, new THREE.Vector3(0, 0, 1), 0, Math.PI * 2, 32),
        [center, radius]
    );
    const geometry = useMemo(() => new THREE.BufferGeometry().setFromPoints(loop), [loop]);
    return (
        <lineLoop raycast={() => null} renderOrder={12}>
            <primitive object={geometry} attach="geometry" />
            <lineBasicMaterial color={color} depthTest={false} transparent opacity={0.9} />
        </lineLoop>
    );
}

function SmooveScope() {
    const { center } = useSketchSmooveTool();
    if (!center) return null;
    return <RadiusCircle center={center.point} radius={center.radius} color="#ff00ff" />;
}

function StampScope() {
    const { center } = useSketchStampTool();
    if (!center) return null;
    return <RadiusCircle center={center.point} radius={center.radius} color="#8844ff" />;
}

function FromContoursScope() {
    useSketchFromContoursTool();
    return null;
}

function DrapeScope() {
    useSketchDrapeTool();
    return null;
}

function AddDetailScope() {
    useSketchAddDetailTool();
    return null;
}
